/*
* 전자책 목록 페이지
*
* /api/viewer/books/my/books API에서 도서 목록을 조회하여 표시합니다.
* 전자책을 클릭하면 해당 전자책의 뷰어 페이지로 이동합니다.
*/

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import apiClient from '../../../core/http/axios';
import { ImageWithFallback } from '../../components/ui/ImageWithFallback';
import StarIcon from '../../components/ui/StarIcon';
import { useFrontAuthStore } from '../../auth/store/authStore';

// 하드코딩 도서 UUID + 학교/학과 매칭 + 폴백 도서 정보
const HARDCODED_BOOK_MAP: {
  book_uuid: string;
  title: string;
  author: string;
  img_url: string;
  schools: { university: string; department: string }[];
}[] = [
  {
    book_uuid: '6c1bb66a-18eb-49ac-9751-4c06283a268f',
    title: '여성심리학 젠더와 다양성을 위하여',
    author: '김연수·김은영·김혜민·박경은·신나영 신홍임·이지영·조윤진·한영주·홍상희',
    img_url: 'https://d2vzlznvn6znt2.cloudfront.net/custom/genbook/book/6c1bb66a-18eb-49ac-9751-4c06283a268f/bb00b7d4-115c-11f1-9d82-0a310bc08283.jpeg',
    schools: [
      { university: '원광보건대', department: '간호학과' },
      { university: '백석대학교', department: '간호학과' },
    ],
  },
  {
    book_uuid: '2645ef2d-3f75-48ae-a7e5-a2074fb1aea9',
    title: '아들러 심리학의 격려 사례집2_격려하는 선생님',
    author: '남은미, 오익수',
    img_url: 'https://d2vzlznvn6znt2.cloudfront.net/custom/genbook/book/2645ef2d-3f75-48ae-a7e5-a2074fb1aea9/ea7600d7-1153-11f1-80df-0a310bc08283.jpeg',
    schools: [
      { university: '대전보건대', department: '간호학과' },
      { university: '백석대학교', department: '간호학과' },
    ],
  },
  {
    book_uuid: '19ca60ae-a855-4bb1-90d8-aa2353a1d4f3',
    title: '노년기 상담',
    author: '김유숙, 이현숙',
    img_url: 'https://d2vzlznvn6znt2.cloudfront.net/custom/genbook/book/19ca60ae-a855-4bb1-90d8-aa2353a1d4f3/7d51a697-1155-11f1-8478-0a310bc08283.jpeg',
    schools: [
      { university: '대전보건대', department: '간호학과' },
      { university: '원광보건대', department: '간호학과' },
    ],
  },
  {
    book_uuid: '5b35b552-4018-4b5d-be99-292872765132',
    title: '쉽게 풀어 쓴 학습 심리',
    author: '김문수',
    img_url: 'https://d2vzlznvn6znt2.cloudfront.net/custom/genbook/book/c2ab4c48-d643-11f0-a411-0a310bc08283/e2cc37cd-ddeb-11f0-832a-0a310bc08283.jpeg',
    schools: [
      { university: '서울대학교', department: '개발학과' },
      { university: '백석대학교', department: '간호학과' },
      { university: '대전보건대', department: '간호학과' },
      { university: '원광보건대', department: '간호학과' },
    ],
  },
  {
    book_uuid: '22b3db40-d837-4d2b-8bb6-224f50030934',
    title: '격려하는 선생님 (신)',
    author: '',
    img_url: 'https://d2vzlznvn6znt2.cloudfront.net/custom/genbook/book/eaea43b6-ddf2-11f0-8467-0a310bc08283/f4c24077-ddf2-11f0-9e9f-0a310bc08283.jpeg',
    schools: [], // 모든 학교에 표시
  },
];

const Container = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing['3xl']};
`;

const Content = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing['3xl']};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.book.color1b};
`;

const FavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--light-pink);
  height: 26px;
  border-radius: ${props => props.theme.borderRadius.full};
  padding: 0 ${props => props.theme.spacing.md};
  margin-left: ${props => props.theme.spacing.sm};
`;

const BannerText = styled.p`
  color: ${props => props.theme.colors.gray[900]};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 196px);
  gap: ${props => props.theme.spacing['xl']};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.lg};
  }
`;

const BookCardWrapper = styled.div`
  position: relative;
  width: 196px;
  height: 262px;
  background: url('/images/book/folder_bg.svg') no-repeat center center;
  cursor: pointer;
  transition: transform 0.2s ease;
  padding: 30px 20px;
  &:hover {
    transform: scale(1.05);
  }
`;

const CardMain = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardTitle = styled.h4`
  color: ${props => props.theme.colors.book.color1b};
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.normal};
  line-height: 1.2;
  height: 3rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  letter-spacing: -0.02em;
  word-break: keep-all;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CardAuthor = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  color: ${props => props.theme.colors.book.color4b};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
`;

const CardDate = styled.p`
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardImageWrapper = styled.div`
  position: relative;
  height: 90px;
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  background-color: ${props => props.theme.colors.gray[200]};
  margin-top: auto;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const STORAGE_KEY = 'book-list-favorites';

const Nodata = styled.div`
  text-align: center;
  padding: 10%;
  color: ${props => props.theme.colors.book.color4b};
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.normal};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.gray[600]};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['3xl']};
  color: ${props => props.theme.colors.error[600]};
`;

interface MyBooksApiItem {
  book_uuid: string;
  title: string;
  author: string;
  img_url: string;
  total_page: number;
  my_page_idx: number;
}

interface MyBooksApiResponse {
  success: boolean;
  data: {
    total: number;
    limit: number;
    offset: number;
    items: MyBooksApiItem[];
  };
}

function loadFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveFavorites(favorites: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...favorites]));
  } catch (e) {
    // console.warn('즐겨찾기 저장 실패:', e);
  }
}

const CardFavButton = styled.button`
  position: absolute;
  bottom: ${props => props.theme.spacing.sm};
  left: ${props => props.theme.spacing.sm};
  z-index: 1;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  padding: 4px;
`;

const BookList: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useFrontAuthStore();
  const [books, setBooks] = useState<MyBooksApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(loadFavorites);
  const [filterByFavorites, setFilterByFavorites] = useState(false);

  const toggleFilterByFavorites = () => {
    setFilterByFavorites(prev => !prev);
  };

  const toggleFavorite = (e: React.MouseEvent, bookUuid: string) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(bookUuid)) {
        next.delete(bookUuid);
      } else {
        next.add(bookUuid);
      }
      saveFavorites(next);
      return next;
    });
  };

  useEffect(() => {
    // localStorage에 저장된 학교/학과 정보 확인
    const selectedLibrary = localStorage.getItem('selected_library');

    if (!selectedLibrary && !isAuthenticated) {
      // 학교/학과 선택도 없고 로그인도 안 되어 있으면 랜딩 페이지로 리다이렉트
      navigate('/');
      return;
    }

    if (selectedLibrary && !isAuthenticated) {
      // 로그인 안 된 상태: 학교/학과 매칭된 하드코딩 도서 표시
      const fetchHardcodedBooks = async () => {
        try {
          setLoading(true);
          setError(null);
          const { universityName, departmentName } = JSON.parse(selectedLibrary);
          const matchedBooks = HARDCODED_BOOK_MAP
            .filter(book =>
              book.schools.length === 0 ||
              book.schools.some(s =>
                universityName.includes(s.university) && departmentName.includes(s.department)
              )
            );

          // API로 실제 데이터 가져오기 시도
          const bookResults = await Promise.allSettled(
            matchedBooks.map(book =>
              apiClient.get(`/viewer/book-info`, { params: { book_uuid: book.book_uuid }, headers: { accept: 'application/json' } })
            )
          );

          // API 성공 시 실제 데이터, 실패 시 하드코딩 폴백 데이터 사용
          const fetchedBooks: MyBooksApiItem[] = matchedBooks.map((book, index) => {
            const result = bookResults[index];
            if (result.status === 'fulfilled' && result.value.data?.success) {
              const d = result.value.data.data;
              return {
                book_uuid: d.book_uuid,
                title: d.title || book.title,
                author: d.author || book.author,
                img_url: d.img_url || book.img_url,
                total_page: d.total_page || 0,
                my_page_idx: d.my_page_idx ?? 0,
              };
            }
            // API 실패 시 하드코딩 데이터로 표시
            return {
              book_uuid: book.book_uuid,
              title: book.title,
              author: book.author,
              img_url: book.img_url,
              total_page: 0,
              my_page_idx: 0,
            };
          });

          setBooks(fetchedBooks);
        } catch {
          setBooks([]);
        } finally {
          setLoading(false);
        }
      };
      fetchHardcodedBooks();
      return;
    }

    // 로그인된 상태: API에서 도서 목록 조회
    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<MyBooksApiResponse>(
          '/viewer/books/my/books?limit=20&offset=0'
        );
        if (response.data.success && response.data.data?.items) {
          setBooks(response.data.data.items);
        } else {
          setBooks([]);
        }
      } catch (err) {
        setError('도서 목록을 불러오는 중 오류가 발생했습니다.');
        // console.error(err);
        setBooks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, [isAuthenticated]);

  const handleBookClick = (bookUuid: string) => {
    if (!isAuthenticated) {
      // 로그인 안 된 상태에서 책 클릭 시 로그인 페이지로 이동 (로그인 후 리스트로 복귀)
      localStorage.setItem('redirect_after_login', '/book/list');
      navigate('/login');
      return;
    }
    navigate(`/api-book/viewer/${bookUuid}/1`);
  };

  const displayedBooks = filterByFavorites
    ? books.filter(book => favorites.has(book.book_uuid))
    : books;


  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingContainer>책 데이터를 불러오는 중...</LoadingContainer>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container>
          <ErrorContainer>{error}</ErrorContainer>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Content>
          <Header>
            <Title>My List</Title>
            {isAuthenticated && (
              <FavButton onClick={toggleFilterByFavorites}>
                <img src={filterByFavorites ? "/images/book/icon_star_on.svg" : "/images/book/icon_star_off.svg"} alt="즐겨찾기 필터" />
              </FavButton>
            )}
            <Banner>
              <BannerText>추가 검색 없이, 우리 학과 교재들을 바로 사용할 수 있어요.</BannerText>
            </Banner>
          </Header>

          {displayedBooks.length > 0 ? (
            <BookGrid>
              {displayedBooks.map((book) => (
                <BookCardWrapper
                  key={book.book_uuid}
                  onClick={() => handleBookClick(book.book_uuid)}
                >
                  <CardMain>
                    <CardTitle>{book.title}</CardTitle>
                    <CardAuthor>{book.author}</CardAuthor>
                    {/* <CardDate>-</CardDate> */}
                    <CardImageWrapper>
                      {book.img_url ? (
                        <ImageWithFallback src={book.img_url} alt={book.title} />
                      ) : null}
                      {isAuthenticated && (
                        <CardFavButton onClick={(e) => toggleFavorite(e, book.book_uuid)}>
                          <img src={favorites.has(book.book_uuid) ? "/images/book/icon_star_on.svg" : "/images/book/icon_star_off.svg"} alt="즐겨찾기" />
                        </CardFavButton>
                      )}
                    </CardImageWrapper>
                  </CardMain>
                </BookCardWrapper>
              ))}
            </BookGrid>
          ) : (
            <Nodata>
              {filterByFavorites ? '즐겨찾기한 도서가 없습니다.' : '등록된 도서가 없습니다.'}
            </Nodata>
          )}
        </Content>
      </Container>
    </Layout>
  );
};

export default BookList;
